import {
  ref,
  get,
  set,
  remove,
  onValue,
  query,
  orderByChild,
  equalTo,
  off,
} from 'firebase/database';
import { rtdb } from '../../config/firebase';
import type { Review } from '../../types';
import { dateToMs, msToDate, snapshotToArray, stripUndefined } from './helpers';

const PATH = 'reviews';

type Unsubscribe = () => void;

const fromRtdb = (id: string, data: any): Review => ({
  ...data,
  id,
  createdAt: msToDate(data.createdAt) as Date,
});

const toRtdb = (review: Review): any => {
  const { id: _id, ...rest } = review;
  return {
    ...rest,
    createdAt: dateToMs(review.createdAt),
  };
};

const sortByCreatedAtDesc = (reviews: Review[]): Review[] =>
  [...reviews].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

export const getAllReviews = async (): Promise<Review[]> => {
  const snapshot = await get(ref(rtdb, PATH));
  return sortByCreatedAtDesc(
    snapshotToArray<Review>(snapshot.val(), fromRtdb)
  );
};

export const getReviewsByHomeId = async (homeId: string): Promise<Review[]> => {
  const q = query(ref(rtdb, PATH), orderByChild('homeId'), equalTo(homeId));
  const snapshot = await get(q);
  return sortByCreatedAtDesc(
    snapshotToArray<Review>(snapshot.val(), fromRtdb)
  );
};

export const getReviewByBookingId = async (
  bookingId: string
): Promise<Review | null> => {
  const q = query(ref(rtdb, PATH), orderByChild('bookingId'), equalTo(bookingId));
  const snapshot = await get(q);
  const reviews = snapshotToArray<Review>(snapshot.val(), fromRtdb);
  return reviews[0] ?? null;
};

export const saveReview = async (review: Review): Promise<void> => {
  await set(ref(rtdb, `${PATH}/${review.id}`), stripUndefined(toRtdb(review)));
};

export const deleteReview = async (reviewId: string): Promise<void> => {
  await remove(ref(rtdb, `${PATH}/${reviewId}`));
};

export const subscribeToReviews = (
  callback: (reviews: Review[]) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  const r = ref(rtdb, PATH);
  const handler = onValue(
    r,
    (snapshot) => {
      callback(
        sortByCreatedAtDesc(snapshotToArray<Review>(snapshot.val(), fromRtdb))
      );
    },
    onError
  );
  return () => off(r, 'value', handler);
};

export const subscribeToHomeReviews = (
  homeId: string,
  callback: (reviews: Review[]) => void
): Unsubscribe => {
  const q = query(ref(rtdb, PATH), orderByChild('homeId'), equalTo(homeId));
  const handler = onValue(q, (snapshot) => {
    callback(
      sortByCreatedAtDesc(snapshotToArray<Review>(snapshot.val(), fromRtdb))
    );
  });
  return () => off(q, 'value', handler);
};

/**
 * Calculate average rating for a home
 */
export const calculateAverageRating = (reviews: Review[]): number => {
  if (reviews.length === 0) return 0;
  const total = reviews.reduce((sum, review) => sum + review.rating, 0);
  return Math.round((total / reviews.length) * 10) / 10;
};
